import { Alert, Button, Stack, Typography } from "@mui/material";
import { FormProvider, SubmitHandler, TextFieldElement, useForm } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { GqlUserUpdatePasswordInput } from "~/__generated__";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import useUserUpdate from "~/modules/user/api/useUserUpdate";

import { ME_GET_USER_INFO } from "../../../auth/api/useMeGetUserInfoQuery";

type PasswordUpdateFormUserData = {
    id: string;
};

type Props = {
    user: PasswordUpdateFormUserData;
    currentPasswordNotRequired?: boolean;
};

export default function PasswordUpdate({ user, currentPasswordNotRequired }: Props) {
    const { t } = useTranslation("user");

    const { t: authT } = useTranslation("auth");
    const { updateUser, error, loading } = useUserUpdate(user.id, [ME_GET_USER_INFO]);

    const formMethods = useForm<GqlUserUpdatePasswordInput>({
        defaultValues: {
            currentPassword: "",
            newPassword: "",
            confirmNewPassword: "",
        },
    });

    const {
        handleSubmit,
        setError,
        reset,
        formState: { errors, isDirty, isSubmitted, isSubmitting },
    } = formMethods;

    const onSubmit: SubmitHandler<GqlUserUpdatePasswordInput> = async data => {
        await updateUser({
            passwordData: data,
        });
        reset();
    };

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <GaiaConfirmCloseDialog blocker={blocker} />
                <Stack spacing={2}>
                    {errors.root && (
                        <Alert severity="error">
                            <Typography>{errors.root.message}</Typography>
                        </Alert>
                    )}

                    {!currentPasswordNotRequired && (
                        <TextFieldElement
                            name="currentPassword"
                            type="password"
                            label={t("userForm.labelCurrentPassword")}
                            required
                        />
                    )}

                    <TextFieldElement
                        name="newPassword"
                        type="password"
                        label={t("userForm.labelNewPassword")}
                        helperText={authT("resetPasswordForm.helperTextNewPassword")}
                        required
                    />

                    <TextFieldElement
                        name="confirmNewPassword"
                        type="password"
                        label={t("userForm.labelConfirmNewPassword")}
                        required
                    />
                </Stack>

                <Button
                    fullWidth
                    sx={{ mt: 3 }}
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={!isDirty}
                    loading={loading}
                >
                    {t("userForm.setPassword")}
                </Button>
            </form>
        </FormProvider>
    );
}
